import useMessage from 'antd/es/message/useMessage'
import CantileverButton from 'components/CantileverButton'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import {
  AssetOverviewDataContext,
  EditSaveCancelButtons,
} from 'features/assetDatabase'
import { StyledCloseIcon } from 'features/assetDatabase/utils/sharedStyles'
import { useRolodexEdit } from 'features/fde'
import { useGeckoLocale } from 'features/locale'
import { useRolodexEntries } from 'features/rolodex'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoEntry,
  GeckoInspection,
  GeckoReport,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

import { AssetVizPreviewer } from './AssetVizPreviewer'
import { useClickOutside } from './hooks/useClickOutside'
import useEscapeKey from './hooks/useEscapeKey'
import { useGeometricPinCoords } from './hooks/useGeometricPinCoords'
import {
  EditingState,
  FormState,
  ImagePreviewerProvider,
} from './ImagePreviewerContext'
import { useImagesContext } from './ImagesContext'
import { ImagesLayerSelect } from './ImagesLayerSelect'
import { ModalImageSide } from './ModalImageSide'
import {
  ISAssetVizContainer,
  ISAssetVizInputSelectRow,
  ISEditSaveCancelButtonsContainer,
  ISFullImageModal,
  ISImageAndAVContainer,
  ISNoInspectionMessage,
  ISSpacer,
  ISStyledSelectDiv,
  ISStyledSelectLabel,
} from './StyledComponents'
import {
  convertCoordstoLocalUnits,
  getComponentInspections,
  getInspection,
  updateGeometricPin,
  updateImageThumbnail,
} from './utils'

export const CloseModalContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
`

interface CoordImagePreviewerProps {
  imageUrl: string
  imageThumbnail: GeckoReport
  onClose: () => void
  componentID?: string
  inspectionID?: string
  confirmDelete: (
    e: React.MouseEvent,
    index: number,
    geometricPins: readonly GeckoEntry[]
  ) => void
  selectedImageIndex: number | null
}

const ButtonRowId = uuidv4()

export const CoordImagePreviewer: React.FC<CoordImagePreviewerProps> = ({
  imageUrl,
  imageThumbnail,
  onClose,
  componentID,
  inspectionID,
  confirmDelete,
  selectedImageIndex,
}) => {
  const [potentialInspections, setPotentialInspections] = useState<
    readonly GeckoInspection[]
  >([])
  const [selectedInspection, setSelectedInspection] =
    useState<GeckoInspection | null>(null)

  const [formState, setFormState] = useState<FormState>(() => ({
    fileName: imageThumbnail
      ? imageThumbnail.data.visibleName.replace(
          `.${imageThumbnail.data.fileType}`,
          ''
        )
      : '',
    fileCaption: imageThumbnail?.data.file_caption || '',
  }))

  const [editingState, setEditingState] = useState<EditingState>({
    fileName: false,
    fileCaption: false,
    coordinates: false,
  })

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null
  )
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })

  const { assetDataObj } = useContext(AssetOverviewDataContext)
  const assetModel = assetDataObj?.assetModel

  const { components, inspections, unit } = useImagesContext()
  const inspection = getInspection(componentID, inspectionID, inspections)

  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const [messageApi, contextHolder] = useMessage()

  const { rolodexPatch } = useRolodexEdit()

  useEscapeKey(onClose)

  const modalRef = useRef<HTMLDivElement>(null)
  const confirmPopupRef = useRef<HTMLDivElement>(null)

  useClickOutside(modalRef, onClose, [confirmPopupRef])

  const { entries: geometricPins } = useRolodexEntries({
    type: RolodexEntryType.GEOMETRIC_PIN,
    tags: { report: imageThumbnail.id },
  })

  const { selectedCoords, setSelectedCoords } = useGeometricPinCoords(
    geometricPins,
    inspections,
    componentID,
    inspectionID
  )
  const { x, y } = selectedCoords
    ? convertCoordstoLocalUnits(selectedCoords, getPreferredMeasurementUnit)
    : { x: null, y: null }

  const isUnit = !!(!inspection && !componentID)
  const isComponentWithoutInspection = !!(componentID && !inspection)
  const getInfoMessage = () => {
    if (isUnit) {
      return 'Cannot set coordinates for a unit image.'
    } else if (isComponentWithoutInspection) {
      return 'Cannot assign coordinates without an existing inspection.'
    } else {
      return ''
    }
  }

  useEffect(() => {
    const isComponent = componentID !== undefined
    const isInspection = inspectionID !== undefined
    if (isComponent) {
      const potenialInspections = getComponentInspections(
        componentID,
        inspections
      )
      setPotentialInspections(potenialInspections)
      setSelectedInspection(potenialInspections[0] ?? null)
    }

    if (isInspection) {
      setPotentialInspections(
        inspections.filter((inspection) => inspection.id === inspectionID)
      )
      setSelectedInspection(
        getInspection(componentID, inspectionID, inspections) ?? null
      )
    }
  }, [componentID, inspectionID, components, inspections])

  const onInspectionSelectChange = (value: string) => {
    const selected = potentialInspections.find((insp) => insp.id === value)
    setSelectedInspection(selected || null)
  }

  const handleClose = () => {
    onClose()
  }

  const handleSave = async () => {
    const fileNameWithExtension = `${formState.fileName}.${imageThumbnail.data.fileType}`
    const thumbnailUpdated = await updateImageThumbnail({
      id: imageThumbnail.id,
      fileName: fileNameWithExtension,
      fileCaption: formState.fileCaption,
      rolodexPatch,
      messageApi,
    })

    const isXCoordValid = x?.num !== undefined && x?.unit !== undefined
    const isYCoordValid = y?.num !== undefined && y?.unit !== undefined

    if (
      thumbnailUpdated &&
      editingState.coordinates &&
      isXCoordValid &&
      isYCoordValid &&
      x &&
      y
    ) {
      await updateGeometricPin({
        x,
        y,
        geometricPins,
        componentID,
        inspectionID,
        unit,
        imageThumbnail,
        inspections,
        rolodexPatch,
        messageApi,
      })
    }

    setEditingState({
      fileName: false,
      fileCaption: false,
      coordinates: false,
    })
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    })
    setPendingDeleteIndex(selectedImageIndex)
  }

  const handleConfirmDelete = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (pendingDeleteIndex !== null && selectedImageIndex !== null) {
      confirmDelete(e as React.MouseEvent, pendingDeleteIndex, geometricPins)
      setPendingDeleteIndex(null)
      onClose()
    }
  }

  const handleCancelDelete = () => {
    setPendingDeleteIndex(null)
  }

  if (!imageThumbnail) {
    return null
  }

  return (
    <>
      {contextHolder}
      <ImagePreviewerProvider
        imageUrl={imageUrl}
        imageThumbnail={imageThumbnail}
        componentID={componentID}
        inspectionID={inspectionID}
        editingState={editingState}
        setEditingState={setEditingState}
        formState={formState}
        setFormState={setFormState}
        handleDeleteClick={handleDeleteClick}
        selectedCoords={selectedCoords}
        inspection={inspection}
        handleSave={handleSave}
        handleClose={handleClose}>
        <ISFullImageModal>
          <ISImageAndAVContainer ref={modalRef}>
            <ModalImageSide />
            <ISAssetVizContainer>
              {isComponentWithoutInspection || isUnit ? (
                <>
                  <ISNoInspectionMessage>
                    {getInfoMessage()}
                  </ISNoInspectionMessage>
                  <ISSpacer />
                  <ISEditSaveCancelButtonsContainer>
                    <EditSaveCancelButtons
                      saveCancelButtons={true}
                      handleSave={handleSave}
                      handleCancel={handleClose}
                      handleEdit={() => {}}
                      formID={ButtonRowId}
                    />
                  </ISEditSaveCancelButtonsContainer>
                </>
              ) : (
                <>
                  {selectedInspection && (
                    <>
                      <ISAssetVizInputSelectRow>
                        <ISStyledSelectDiv>
                          <ISStyledSelectLabel>Inspection:</ISStyledSelectLabel>
                          <ImagesLayerSelect
                            label='Inspection'
                            options={potentialInspections.map(
                              (inspection) => inspection.id
                            )}
                            value={selectedInspection?.id || ''}
                            onChange={onInspectionSelectChange}
                          />
                        </ISStyledSelectDiv>
                      </ISAssetVizInputSelectRow>
                      <AssetVizPreviewer
                        assetModel={assetModel}
                        selectedInspection={selectedInspection}
                        setSelectedCoords={setSelectedCoords}
                        setIsEditingCoordinates={(value) => {
                          setEditingState((prev) => ({
                            ...prev,
                            coordinates: value,
                          }))
                        }}
                      />
                    </>
                  )}
                </>
              )}
            </ISAssetVizContainer>
          </ISImageAndAVContainer>

          {pendingDeleteIndex !== null && (
            <div ref={confirmPopupRef}>
              <ConfirmDeletePopup
                entityName='image'
                popupPosition={popupPosition}
                cancelDelete={handleCancelDelete}
                confirmDelete={handleConfirmDelete}
              />
            </div>
          )}

          <CloseModalContainer>
            <CantileverButton iconOnly onClick={onClose}>
              <StyledCloseIcon />
            </CantileverButton>
          </CloseModalContainer>
        </ISFullImageModal>
      </ImagePreviewerProvider>
    </>
  )
}
